import { useEffect } from 'react';

export interface PageMetadata {
  /** Document title; omit to leave the current one. */
  title?: string | null;
  /** Favicon / home-screen icon URL; omit to leave the app icon. */
  iconUrl?: string | null;
}

const ICON_SELECTORS = ['link[rel="icon"]', 'link[rel="apple-touch-icon"]'];

/**
 * Per-page document title and icon, restored on unmount.
 *
 * Public pages (an event invitation, a questionnaire) are their own
 * destination rather than a screen inside the app, so they should read as the
 * event in the browser tab, in the tab switcher, and on the phone home screen
 * when a guest saves the link. Purely a browser-level concern — link previews
 * in chat apps come from the server-rendered Open Graph tags, since crawlers
 * never run this code.
 */
export function usePageMetadata({ title, iconUrl }: PageMetadata): void {
  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);

  useEffect(() => {
    if (!iconUrl) return;
    const links = ICON_SELECTORS.flatMap((selector) =>
      Array.from(document.querySelectorAll<HTMLLinkElement>(selector)),
    );
    if (links.length === 0) return;
    const previous = links.map((link) => link.href);
    links.forEach((link) => {
      link.href = iconUrl;
    });
    return () => {
      links.forEach((link, index) => {
        link.href = previous[index];
      });
    };
  }, [iconUrl]);
}
